import { getNestedValue, setNestedValue } from './base';

const GLOBAL_VARIABLES_KEY = 'tnbe_global_variables';

/**
 * Get global variables from localStorage
 */
export const getGlobalVariables = (): Record<string, any> => {
  try {
    const stored = localStorage.getItem(GLOBAL_VARIABLES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading global variables from localStorage:', error);
    return {};
  }
};

/**
 * Set global variables to localStorage
 */
export const setGlobalVariables = (variables: Record<string, any>): void => {
  try {
    localStorage.setItem(GLOBAL_VARIABLES_KEY, JSON.stringify(variables));
  } catch (error) {
    console.error('Error saving global variables to localStorage:', error);
  }
};

/**
 * Get a specific global variable by path (e.g., 'c.user.name')
 */
export const getGlobalVariable = (path: string): any => {
  const globalVars = getGlobalVariables();
  return getNestedValue(globalVars, path);
};

/**
 * Set a specific global variable by path (e.g., 'c.user.name', 'John')
 */
export const setGlobalVariable = (path: string, value: any): void => {
  const globalVars = getGlobalVariables();
  setNestedValue(globalVars, path, value);
  setGlobalVariables(globalVars);
};

/**
 * Check if a variable path starts with 'c.' or is exactly 'c' (global variable prefix)
 */
export const isGlobalVariablePath = (path: string): boolean => {
  return path === 'c' || path.startsWith('c.');
};

/**
 * Enhanced variable getter that supports both local and global variables
 */
export const getVariableValue = (
  path: string, 
  localVariables: Record<string, any> = {}, 
  localVariableData: Record<string, any> = {}
): any => {
  if (isGlobalVariablePath(path)) {
    return getGlobalVariable(path);
  }
  
  // Try local variables first, then localVariableData
  return getNestedValue(localVariables, path) || getNestedValue(localVariableData, path);
};

/**
 * Enhanced variable setter that supports both local and global variables
 */
export const setVariableValue = (
  path: string, 
  value: any, 
  updateLocalVariable?: (key: string, value: any) => void
): void => {
  if (isGlobalVariablePath(path)) {
    setGlobalVariable(path, value);
  } else if (updateLocalVariable) {
    updateLocalVariable(path, value);
  }
};
